import React, { useState, useEffect } from 'react';
import { LogOut, Building2 } from 'lucide-react';
import logoutManager from '../../logoutManager';

const HODHeader = ({ orgInfo = {} }) => {
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [logoError, setLogoError] = useState(false);

  // State
  const [institutionName, setInstitutionName] = useState('');
  const [logoUrl, setLogoUrl] = useState(null);
  const [tenantId, setTenantId] = useState('');

  // Load data from props or localStorage
  useEffect(() => {
    // If orgInfo is provided as prop, use it (preferred)
    if (orgInfo?.name) {
      setInstitutionName(orgInfo.name);
      setLogoUrl(orgInfo.logoUrl || null);
      setLogoError(false);
      
      // Get tenant ID from localStorage
      const id = localStorage.getItem('tenantId');
      if (id) setTenantId(id);
      return;
    }

    // Fallback: Load from localStorage
    try {
      const t = JSON.parse(localStorage.getItem('tenantData') || '{}');
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      
      let id = localStorage.getItem('tenantId');
      if (!id) id = u.tenantId || u.organizationId || t.institutionId || t.id;

      if (t.institutionName || t.name) {
        setInstitutionName(t.institutionName || t.name);
      }
      if (t.logoUrl || t.logo) {
        setLogoUrl(t.logoUrl || t.logo);
      }
      if (id) setTenantId(id);
    } catch (err) {
      console.error('❌ [HODHeader] Storage parse error:', err);
    }
  }, [orgInfo]);

  // Reset logo error when logoUrl changes
  useEffect(() => {
    setLogoError(false);
  }, [logoUrl]);

  // Logout using the centralized logoutManager
  const handleLogout = async () => {
    setIsLoggingOut(true);
    setShowLogoutConfirm(false);
    
    const redirectPath = '/';

    const result = await logoutManager.logout({
      redirectTo: redirectPath,
      showToast: false,
      onSuccess: () => {
        console.log('✅ HOD header logged out successfully');
      }
    });

    if (!result.success) {
      // Fallback: if logout fails, still redirect
      setTimeout(() => {
        window.location.href = redirectPath;
      }, 500);
    }

    setIsLoggingOut(false);
  };

  const displayName = institutionName || 'Horizon Independent PU College';
  const hasValidLogo = !logoError && logoUrl && String(logoUrl).trim() !== '';

  return (
    <>
      <header className="sticky top-0 z-40 w-full bg-white dark:bg-gray-800 border-b border-slate-200 dark:border-gray-700 shadow-xs flex flex-col">
        {/* Accent Bar */}
        <div className="h-0.5 w-full bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 shrink-0" />

        {/* Navbar */}
        <div className="w-full px-3 sm:px-6 h-14 flex items-center justify-between gap-3">
          
          {/* Identity: Logo & Institution Name */}
          <div className="flex items-center gap-2.5 min-w-0 max-w-[75%]">
            <div className="shrink-0">
              {hasValidLogo ? (
                <div className="h-9 w-9 rounded-xl border border-slate-200 dark:border-gray-700 p-0.5 bg-white dark:bg-gray-900 flex items-center justify-center shadow-xs overflow-hidden">
                  <img
                    src={logoUrl}
                    alt={displayName}
                    className="h-full w-full object-contain rounded-lg"
                    onError={() => {
                      setLogoError(true);
                    }}
                  />
                </div>
              ) : (
                <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-600 flex items-center justify-center text-white shadow-xs font-black text-sm">
                  {displayName.charAt(0).toUpperCase()}
                </div>
              )}
            </div>

            <h1 className="text-xs sm:text-sm font-black text-slate-900 dark:text-white truncate tracking-tight">
              {displayName}
            </h1>
          </div>

          {/* Logout Button */}
          <div className="shrink-0 flex items-center gap-2">
            <button
              onClick={() => setShowLogoutConfirm(true)}
              disabled={isLoggingOut}
              className="flex items-center justify-center gap-1.5 h-8 px-2.5 sm:px-3 bg-slate-50 dark:bg-rose-900/20 hover:bg-rose-50 dark:hover:bg-rose-900/40 text-slate-700 dark:text-rose-300 hover:text-rose-600 border border-slate-200 dark:border-rose-800/60 rounded-lg transition-colors disabled:opacity-60 text-xs font-bold"
            >
              {isLoggingOut ? (
                <svg className="animate-spin h-3.5 w-3.5 text-rose-600" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" strokeDasharray="32" />
                </svg>
              ) : (
                <>
                  <span className="hidden xs:inline">Logout</span>
                  <LogOut size={13} />
                </>
              )}
            </button>
          </div>
        </div>

        {/* Sub-strip with Institution ID */}
        {tenantId && (
          <div className="border-t border-slate-100 dark:border-gray-700/60 bg-slate-50/60 dark:bg-gray-900/40 px-3 sm:px-6 py-1 flex items-center gap-4 text-[10px] text-slate-500 dark:text-slate-400">
            <div className="flex items-center gap-1 min-w-0">
              <Building2 size={10} className="text-slate-400 shrink-0" />
              <p className="truncate">
                Institution ID: <span className="text-slate-800 dark:text-slate-200 font-bold">{tenantId}</span>
              </p>
            </div>
          </div>
        )}
      </header>

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-xs flex items-center justify-center z-[1000] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-xs w-full shadow-2xl border border-slate-100 dark:border-gray-700 p-5 text-center">
            <div className="w-12 h-12 bg-rose-50 dark:bg-rose-900/30 rounded-full flex items-center justify-center mx-auto mb-3 overflow-hidden p-1">
              <LogOut size={20} className="text-rose-600 dark:text-rose-400" />
            </div>
            <h3 className="text-sm font-bold text-slate-900 dark:text-white mb-1">Confirm Session Logout</h3>
            <p className="text-slate-500 dark:text-slate-400 text-xs mb-4">
              Are you sure you want to exit your HOD desk session for <strong className="text-slate-800 dark:text-slate-200">{displayName}</strong>?
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setShowLogoutConfirm(false)}
                disabled={isLoggingOut}
                className="flex-1 py-2 text-xs font-bold bg-slate-100 dark:bg-gray-700 hover:bg-slate-200 text-slate-700 dark:text-slate-200 rounded-xl transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleLogout}
                disabled={isLoggingOut}
                className="flex-1 py-2 text-xs font-bold bg-rose-600 hover:bg-rose-700 text-white rounded-xl transition-colors disabled:opacity-60 flex items-center justify-center space-x-1"
              >
                {isLoggingOut ? 'Exiting...' : 'Logout'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default HODHeader;